/**
 * Currency Context
 * Provides dynamic currency detection and conversion throughout the app
 */
import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import {
  detectUserCurrency,
  convertCurrency,
  formatPrice,
  getExchangeRates,
  getSupportedCurrencies,
  clearCurrencyCache,
  DEFAULT_CURRENCY
} from '../lib/currencyService';

const CurrencyContext = createContext(null);

export function CurrencyProvider({ children }) {
  const [userCurrency, setUserCurrency] = useState(DEFAULT_CURRENCY);
  const [exchangeRates, setExchangeRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [manualOverride, setManualOverride] = useState(null);

  // Initialize currency detection
  useEffect(() => {
    const init = async () => {
      try {
        // Check for manual override in localStorage
        const savedCurrency = localStorage.getItem('afriqoma_preferred_currency');
        if (savedCurrency) {
          try {
            const parsed = JSON.parse(savedCurrency);
            setManualOverride(parsed);
            setUserCurrency(parsed);
          } catch (error) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('[CurrencyContext] Invalid saved currency:', error.message);
            }
            localStorage.removeItem('afriqoma_preferred_currency');
          }
        }

        // Detect currency from IP if no manual override
        if (!savedCurrency) {
          const detected = await detectUserCurrency();
          setUserCurrency(detected);
        }

        // Fetch exchange rates
        const rates = await getExchangeRates();
        setExchangeRates(rates);
      } catch (error) {
        // Currency init failed - log in dev, use defaults
        if (process.env.NODE_ENV === 'development') {
          console.error('[CurrencyContext] Init failed:', error.message);
        }
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  // Convert USD to user's currency
  const convertToLocal = useCallback((amountInUSD) => {
    if (!amountInUSD || isNaN(amountInUSD)) return 0;
    
    const rate = exchangeRates[userCurrency.code] || 1;
    return amountInUSD * rate;
  }, [exchangeRates, userCurrency.code]);

  // Convert from any currency to user's currency
  const convert = useCallback(async (amount, fromCurrency = 'USD') => {
    return await convertCurrency(amount, fromCurrency, userCurrency.code);
  }, [userCurrency.code]);

  // Format price in user's currency
  const formatLocal = useCallback((amountInUSD, options = {}) => {
    const localAmount = convertToLocal(amountInUSD);
    return formatPrice(localAmount, userCurrency.code, options);
  }, [convertToLocal, userCurrency.code]);

  // Format with both USD and local currency
  const formatDual = useCallback((amountInUSD, options = {}) => {
    if (userCurrency.code === 'USD') {
      return formatPrice(amountInUSD, 'USD', options);
    }
    
    const localAmount = convertToLocal(amountInUSD);
    const usdFormatted = formatPrice(amountInUSD, 'USD', { decimals: 0 });
    const localFormatted = formatPrice(localAmount, userCurrency.code, { decimals: 0 });
    
    return `${localFormatted} (${usdFormatted})`;
  }, [convertToLocal, userCurrency.code]);

  // Change user's preferred currency
  const setCurrency = useCallback((currency) => {
    setUserCurrency(currency);
    setManualOverride(currency);
    localStorage.setItem('afriqoma_preferred_currency', JSON.stringify(currency));
  }, []);

  // Reset to auto-detected currency
  const resetCurrency = useCallback(async () => {
    localStorage.removeItem('afriqoma_preferred_currency');
    clearCurrencyCache();
    setManualOverride(null);
    
    const detected = await detectUserCurrency();
    setUserCurrency(detected);
  }, []);

  const contextValue = useMemo(() => ({
    // State
    currency: userCurrency,
    currencyCode: userCurrency.code,
    currencySymbol: userCurrency.symbol,
    currencyName: userCurrency.name,
    loading,
    isAutoDetected: !manualOverride,
    exchangeRates,
    
    // Methods
    convertToLocal,
    convert,
    formatLocal,
    formatDual,
    formatPrice: (amount, code, opts) => formatPrice(amount, code || userCurrency.code, opts),
    setCurrency,
    resetCurrency,
    getSupportedCurrencies
  }), [
    userCurrency, loading, manualOverride, exchangeRates,
    convertToLocal, convert, formatLocal, formatDual, setCurrency, resetCurrency
  ]);

  return (
    <CurrencyContext.Provider value={contextValue}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}

export default CurrencyContext;
